import {
  CASE_HISTORY_TOOL,
  HISTORY_QUICK_QUESTIONS,
} from '@components/ActivityHistory/assistant/quickQuestions'
import { activityHistoryFromPath } from '@components/ActivityHistory/routes'

import type { AssistantPlacement, AssistantPlacementId } from './types'

/** The composer's placeholder in the drawer and on the standalone page. */
export const DEFAULT_PLACEHOLDER = 'Ask a question, or type / for commands…'

/**
 * Every place K-Assist is shown in, as static data (no API call). The server keeps its own
 * registry under the same keys for what it narrows (Phase 2); nothing here grants a tool.
 */
export const ASSISTANT_PLACEMENTS: Record<
  AssistantPlacementId,
  AssistantPlacement
> = {
  // The drawer as it is today. The standalone page (/view/assistant) uses it too, with its own
  // `standalone` prop (no Close, no Open in new window).
  'default-drawer': {
    id: 'default-drawer',
    displayName: 'K-Assist',
    contextSource: 'screen',
    conversationScope: 'shared',
    header: {
      variant: 'full',
      subtitle: 'default',
      buttons: {
        newChat: true,
        guide: true,
        history: true,
        openInNewWindow: true,
        openInDrawer: false,
      },
    },
    welcome: {
      greeting: true,
      briefing: true,
      guideLink: true,
      tryAsking: true,
      intro: null,
    },
    composer: {
      slashCommands: true,
      mentions: true,
      voice: true,
      expand: true,
      keyboardHint: true,
      placeholder: DEFAULT_PLACEHOLDER,
      contextChip: 'removable',
    },
    requiresTools: [],
    // No `placement` is sent: prompt snapshots and hashes stay exactly as today.
    serverFocus: false,
  },

  // The pane of the Case history (the modal and the full page /view/activity-history/IME/{id}).
  'case-history': {
    id: 'case-history',
    displayName: 'Case history',
    contextSource: 'host',
    conversationScope: 'placement',
    header: {
      variant: 'compact',
      subtitle: 'context', // "Case history · IME-10234"
      buttons: {
        newChat: true,
        guide: false,
        history: false,
        openInNewWindow: false,
        openInDrawer: true,
      },
    },
    welcome: {
      greeting: false,
      briefing: false,
      guideLink: false,
      tryAsking: false,
      intro: null,
    },
    composer: {
      slashCommands: false,
      mentions: false,
      voice: true,
      expand: false,
      keyboardHint: false,
      placeholder: "Ask about this case's history…",
      contextChip: 'fixed',
    },
    quickQuestions: {
      items: HISTORY_QUICK_QUESTIONS,
      label: "Ask about this case's history",
      // Wrap when empty, one scrolling row after (as today).
      when: 'always',
    },
    requiresTools: [CASE_HISTORY_TOOL],
    // "Open case history" of this same case leads back here; another case's history is still shown.
    hidesLink: (route, context) =>
      !!context &&
      activityHistoryFromPath(route)?.id.toLowerCase() === context.id,
    // Phase 2: true (the server narrows the toolset to the case's history).
    serverFocus: false,
  },
}

/** The config of a placement. */
export const getPlacement = (id: AssistantPlacementId): AssistantPlacement =>
  ASSISTANT_PLACEMENTS[id]
